import { useId, type ReactNode } from "react";

/**
 * One named group inside a library catalog, with its own creation action
 * (#908).
 *
 * The reference's BG-sets catalog is the shape this exists for: `SET FILES`
 * over "Shared prompt and extraction files…" with `New set file` at its right,
 * then `DOMAINS` over its own sentence with `New domain` at its right. The
 * section header above them carries no creation control at all, because
 * neither control belongs to the section as a whole — each belongs to the kind
 * of asset its group lists.
 *
 * ONE COMPONENT RATHER THAN ONE PER FAMILY. #857/#858/#859 build the BG groups,
 * #863 the Summary ones and #910 Rules; each of those leaves needs a group
 * heading, a sentence, an action at the end of the same row and a body, and
 * three private copies of that would be three different row heights and three
 * different heading levels on catalogs a clinician reads side by side. So the
 * shell publishes the slot and the leaves fill it.
 *
 * A `region` WITH A REAL NAME. The heading is `h2` under the section's `h1`
 * (`LibrarySectionHeader`), and `aria-labelledby` points the landmark at that
 * same heading rather than repeating the text in an `aria-label` — one name, so
 * the two cannot drift. That is what lets a screen-reader user move between
 * `Set files` and `Domains` by landmark or by heading, which is how the
 * reference's two groups read visually.
 */
export function CatalogGroup({
  title,
  description,
  actions,
  children,
}: {
  /** The group's product name — "Set files", "Domains", "Alert matrices". */
  title: string;
  /** One sentence saying what the group holds. */
  description?: string;
  /** This group's own creation control, at the end of the heading row. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        {/*
          `flex-1` — the heading block takes the slack instead of ASKING for
          its content's width (#859, the first consumer of `description` and
          `actions` together).

          Without it this block's hypothetical width is its longest line, so a
          one-sentence description as short as "Each domain is an
          independently versioned set of intake prompts. Open a domain to edit
          them." exceeded what the content area leaves beside the action and
          `flex-wrap` put `New domain` on its own line below — a second row of
          height, and not where the reference draws it ("`DOMAINS` over its own
          sentence with `New domain` at its right", above). Flex decides
          wrapping on the BASIS rather than on the width an item settles at, so
          a `flex-1` text block absorbs the slack and the action stays on the
          heading row. `LibraryCatalog`'s groups pass no description and are
          unaffected either way.
        */}
        <div className="min-w-0 flex-1">
          <h2
            id={headingId}
            className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>

        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>

      {children}
    </section>
  );
}
